import React, { useState, useEffect, useRef } from "react";
import { TranslationHint } from "../../helper/TranslationHint";
import { Modal, Form, Input, Button, Select, DatePicker } from "antd";
import { callApi } from "../../api/apiCaller";
import { GreenNotify, RedNotify } from "../../helper/helper";
import routes from "../../api/routes";
import { mapLinkValidator, MAP_LINK_HINT } from "../../helper/mapLink";
import { Buffer } from "buffer";
import { PLACEHOLDER_IMAGE } from "../../helper/placeholderImage";
const AWS = require("aws-sdk");

AWS.config.update({
  region: process.env.REACT_APP_AWS_REGION,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
});

const AddCategory = ({ visible, recordData, toggleModal }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [roots, setRoots] = useState([]);
  const [stages, setStages] = useState([]);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);
  const { Option } = Select;

  // Title/description are stored as { en, es, ... } translation objects on the backend.
  // A plain <Input> can only display a string, so pick one language to show/edit.
  const displayText = (val) =>
    typeof val === "string"
      ? val
      : val?.en || val?.es || Object.values(val || {})[0] || "";

  useEffect(() => {
    if (visible && recordData) {
      form.setFieldsValue({
        title: displayText(recordData?.title),
        description: displayText(recordData?.description),
        type: recordData?.type,
        eventDateTime: recordData?.eventDateTime,
        image: recordData.image,
        location: recordData.location,
        route: recordData.route,
        stage: recordData.stage,
      });
      setImage(recordData.image || PLACEHOLDER_IMAGE);
    }
  }, [visible, recordData, form]);

  useEffect(() => {
    if (visible) {
      callApi(
        "GET",
        `${routes.getAllRoots}`,
        null,
        setIsLoading,
        (res) => {
          if (res.status === 200) {
            setRoots(res.data.routes);
          } else {
            RedNotify("Error Occurred. Please try again later.");
          }
        },
        (error) => {
          RedNotify(
            "An error occurred during the API call. Please try again later.",
            error
          );
        }
      );
    }
  }, [visible]);

  const handleRouteChange = (value) => {
    const queryParam = encodeURIComponent(JSON.stringify({ route: value }));
    callApi(
      "GET",
      `${routes.getStages}?query=${queryParam}`,
      null,
      setIsLoading,
      (res) => {
        if (res.status === 200) {
          setStages(res.data.stages);
        } else {
          RedNotify("Error Occurred. Please try again later.");
        }
      },
      (error) => {
        RedNotify(
          "An error occurred during the API call. Please try again later.",
          error
        );
      }
    );
  };

  const S3 = new AWS.S3();
  const uploadImageOnS3 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const params = {
          Bucket: "goodeway",
          Key: `${10000 + Math.round(Math.random() * 10000)}.png`,
          Body: Buffer.from(
            reader.result.replace(/^data:image\/\w+;base64,/, ""),
            "base64"
          ),
        };
        try {
          const res = await S3.upload(params).promise();
          resolve(res.Location);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = (event) => {
    const fileList = event.target.files;
    if (fileList.length > 0) {
      const file = fileList[0];
      uploadImageOnS3(file)
        .then((url) => {
          setImage(url);
          setFileError("");
        })
        .catch((error) => {
          setFileError("Error uploading file. Please try again.");
          console.error("Error uploading to S3:", error);
        });
    } else {
      setFileError("Please select a file.");
    }
  };

  const handleSave = () => {
    // Disable the button up front so a slow save cannot be double-submitted; validation
    // failures re-enable it in the .catch below.
    setIsLoading(true);
    form
      .validateFields()
      .then((values) => {
        // Converted for every type now that every type can carry a date. The schema stores
        // eventDateTime as a Number, and the picker hands back a date object.
        if (values.eventDateTime) {
          values.eventDateTime = new Date(values.eventDateTime).getTime();
        }
        const requestBody = { ...values, image };
        callApi(
          "POST",
          `${routes.createBusiness}`,
          requestBody,
          setIsLoading,
          (res) => {
            if (res.status === 200) {
              GreenNotify("Point Of Interest Created Successfully");
              toggleModal();
            } else {
              RedNotify("Error Occurred. Please try again later.");
            }
          },
          (error) => {
            RedNotify(
              "An error occurred during the API call. Please try again later.",
              error
            );
          }
        );
      })
      .catch((errorInfo) => {
        setIsLoading(false);
        console.error("Validation Error:", errorInfo);
      });
  };

  return (
    <Modal
      title={"Create Business"}
      visible={visible}
      onCancel={toggleModal}
      footer={[
        <Button key="cancel" onClick={toggleModal}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Title"
          extra={<TranslationHint />}
          name="title"
          rules={[{ required: true, message: "Please enter the title" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Description"
          extra={<TranslationHint />}
          name="description"
          rules={[{ required: true, message: "Please enter the description" }]}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
        </Form.Item>
        <Form.Item
          label="Location"
          name="location"
          rules={[
            { required: true, message: "Please enter the location" },
            { validator: mapLinkValidator },
          ]}
          extra={MAP_LINK_HINT}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Route"
          name="route"
          rules={[{ required: true, message: "Please select a Route" }]}
        >
          <Select placeholder="Select a Route" onChange={handleRouteChange}>
            {roots.map((root) => (
              <Option key={root._id} value={root._id}>
                {root.title}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Stage"
          name="stage"
          rules={[{ required: true, message: "Please select stage" }]}
        >
          <Select placeholder="Select a stage">
            {stages.map((stage) => (
              <Option key={stage._id} value={stage._id}>
                {stage.title}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true, message: "Please select the type" }]}
        >
          <Select placeholder="Select Type">
            <Option value="Event">Event</Option>
            <Option value="LocalShop">LocalShop</Option>
            <Option value="Resturant">Resturant</Option>
            <Option value="LandMark">LandMark</Option>
            <Option value="Church">Church</Option>
          </Select>
        </Form.Item>
        {/* Offered for every type, not just Event. A shop, restaurant, landmark or church
            saved without one has no date to show in the app, which is why the detail
            screen rendered "Invalid Date" when one was opened from a notification.

            Optional rather than required: a landmark has no meaningful date, and making it
            mandatory would block those records from being saved at all. The app omits the
            date line when there is none. */}
        <Form.Item label="Date & Time" name="eventDateTime">
          <DatePicker
            showTime={{ showNow: true }}
            format="YYYY-MM-DD hh:mm:ss A"
          />
        </Form.Item>
        <div
          onClick={() => fileInputRef.current.click()}
          style={{ marginBottom: "2rem" }}
        >
          <h2>Image</h2>
          {/* The only upload slot that had no class and sized itself with an inline
              style, so it rendered differently from every other form. */}
          <div className="add-product-modal-image">
            <img src={image || PLACEHOLDER_IMAGE} alt="Selected" />
          </div>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          {fileError && <p style={{ color: "red" }}>{fileError}</p>}
        </div>
      </Form>
    </Modal>
  );
};

export default AddCategory;
